/**
 * Adapter: one resolved cabinet wall (+ the appliances / openings on it) →
 * a single-segment cabinet-run `run` and `ctx` ready for
 * `cabinet-runs/fillRun`.
 *
 * Why: the auto kitchen path (`materializeCandidate`) emits ONE coarse
 * `lower-cabinet` box per free wall span, with no bay-type variety and no
 * per-bay editing. The manual cabinet-run engine (`cabinet-runs/fillRun`)
 * packs a span into realistic ≤36" modules (with drawer / sink / filler bay
 * types and per-bay overrides). Both engines already operate in the SAME
 * canonical wall frame — `wallMath.getWallAxes`, with `wallNormal` = the left
 * rotation of bottomLeft→bottomRight pointing into the room — so converting an
 * auto cabinet wall into a run needs NO coordinate transform: we just span the
 * wall with one segment and translate its appliance placements into run
 * appliance anchors.
 *
 * Scope (v1): a single straight wall → a single-segment run. Multi-wall L/U
 * runs (so `fillRun`'s corner planner owns the corners too) are a follow-up;
 * for now the caller keeps corners on the existing `materializeCandidate` path.
 *
 * Pure. See claude-docs/2026-06-30-cabinet-abilities-into-auto-kitchen.md.
 */

const { getWallAxes } = require('../wallMath');

// Auto `placement.applianceType` → the manual engine's appliance type. The
// manual engine derives the footprint from APPLIANCE_FOOTPRINTS by type
// (`appliances.footprintFor`), so only these types are fillable through it.
//
// A WALL OVEN maps to `tallUnit`. It is not built by the run — `tallUnit` is an
// ANCHORED bay, a span the fill packs around and never through, which is
// exactly the oven column's contract (the object keeps its own height and its
// WALL_OVEN_BOTTOM_Y seat). Before this it mapped to nothing, so `toRuns`
// dropped it and the derived run carried NO reservation at all: the first edit
// of a generated kitchen packed base cabinets and uppers straight through the
// oven column the generator had reserved.
const APPLIANCE_TYPE_MAP = {
  sink: 'sink',
  // The canonical KITCHEN sink type (`shared-lib/sink-types`). Bare `sink` is
  // the GENERIC type, NOT "the bathroom one" — this comment used to say that
  // and it is false; there is no `bathroom-sink` type, and a legacy kitchen
  // sink is `{ type: 'sink', sinkType: 'kitchen' }`. See #1505. Scanned
  // kitchens are almost entirely `kitchen-sink` — so omitting it left the
  // scan-run deriver unable to see a sink at all, and a run that does not
  // RESERVE its sink packs cabinets straight through it.
  'kitchen-sink': 'sink',
  dishwasher: 'dishwasher',
  range: 'range',
  stove: 'range',
  // NOT 'range'. A drop-in cooktop is cut into the counter over a base
  // cabinet; a range stands on the floor and owns its whole span. Mapping the
  // two together gave every scanned cooktop a range-shaped VOID — no cabinet
  // under it, no countertop over it, and a counter break either side
  // (5 of the 6 cooktops in the fixture corpus). See `HOSTED_BAY_KINDS`.
  cooktop: 'cooktop',
  fridge: 'fridge',
  refrigerator: 'fridge',
  'wall-oven': 'tallUnit',
  wallOven: 'tallUnit',
  // A tall piece a layout names outright — option #1 hands the scan runs'
  // pantries and oven columns over this way (`tallUnitPlacementsFor`).
  tallUnit: 'tallUnit',
};

/** The run appliance type for an auto placement type, or null if unsupported. */
function toRunApplianceType(applianceType) {
  return APPLIANCE_TYPE_MAP[applianceType] || null;
}

/**
 * `toRunApplianceType` over an OBJECT: the `type` string when present, else
 * the first `category` key the run vocabulary recognises. 7 of 80 corpus
 * appliance objects carry category-only identity (incl. a sink and a stove);
 * a type-only read gave them no bay reservation and the fill packed cabinets
 * straight through them (round-2 audit M16).
 */
function toRunApplianceTypeForObject(o) {
  const own = toRunApplianceType(String(o?.type || '').toLowerCase());
  if (own) return own;
  const cat = o?.category;
  if (cat && typeof cat === 'object') {
    for (const key of Object.keys(cat)) {
      if (!cat[key]) continue;
      const t = toRunApplianceType(String(key).toLowerCase());
      if (t) return t;
    }
  }
  return null;
}

/**
 * @param {Object} input
 * @param {Object} input.wall            Resolved engine wall: `{ id,
 *   alignment: { bottomLeft, bottomRight } }` already in the canonical
 *   inward-normal frame (as `materializeCandidate`'s `walls` are).
 * @param {Array}  [input.placements]    Auto appliance placements (the whole
 *   kitchen's; we filter to this wall). Each: `{ wallId, applianceId,
 *   applianceType, range: { start, end } }`.
 * @param {Array}  [input.openings]      Flat opening list for `ctx.openings`
 *   (`{ id, parentWallIds, center, dimensions, _kind? }`).
 * @param {Array}  [input.walls]         All engine walls, for `ctx.walls`
 *   (adjacent-opening + tall-item checks). Defaults to `[wall]`.
 * @param {number} input.floorY
 * @param {number} [input.cabinetDepth]
 * @param {Object} [input.roomAppearance]
 * @param {boolean}[input.uppers]        `false` to suppress the upper pass.
 * @param {Array}  [input.bayOverrides]  Per-bay overrides (Phase 2 editing).
 * @param {Object} [input.appearance]    Run-scope appearance bundle.
 * @param {string} [input.runId]
 * @returns {{ run: Object, ctx: Object, skippedAppliances: Object[] } | null}
 *   null when the wall alignment is degenerate. `skippedAppliances` are the
 *   placements on this wall the run engine can't own (e.g. wall ovens) — the
 *   caller must place them separately.
 */
function buildRunForWall({
  wall,
  placements = [],
  openings = [],
  walls = null,
  floorY,
  cabinetDepth,
  roomAppearance,
  uppers,
  bayOverrides,
  appearance,
  runId,
}) {
  const axes = getWallAxes(wall);
  if (!axes) return null;

  const appliances = [];
  const skippedAppliances = [];
  for (const p of placements) {
    if (p.wallId !== wall.id) continue;
    if (!p.range) continue;
    const type = toRunApplianceType(p.applianceType);
    if (!type) {
      skippedAppliances.push(p);
      continue;
    }
    appliances.push({
      id: p.applianceId,
      type,
      segmentIndex: 0,
      centerParam: (p.range.start + p.range.end) / 2,
    });
  }

  const run = {
    id: runId || `autorun_${wall.id}`,
    segments: [{ wall, startParam: 0, endParam: axes.wallLen }],
    appliances,
  };
  if (Array.isArray(bayOverrides)) run.bayOverrides = bayOverrides;
  if (appearance) run.appearance = appearance;
  if (uppers === false) run.uppers = false;

  const ctx = {
    floorY,
    openings,
    walls: walls || [wall],
  };
  if (cabinetDepth != null) ctx.cabinetDepth = cabinetDepth;
  if (roomAppearance) ctx.roomAppearance = roomAppearance;

  return { run, ctx, skippedAppliances };
}

module.exports = {
  buildRunForWall,
  toRunApplianceType,
  toRunApplianceTypeForObject,
  APPLIANCE_TYPE_MAP,
};
